import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  Applicant,
  Application,
  MakeDecisionRequirements
} from "types/application";
import {
  ApplicationStatus,
  Invitee,
  PartnerFeatures,
  PropertyKind
} from "shared/types";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { useOwnerReportsEnabled } from "shared/hooks/use-owner-reports-flag";
import { useAuthContext } from "providers/Auth";
import { usePopoverActions } from "views/applications-id/components/LayoutHeader/hooks/usePopoverActions";
import { BoomFinanceLinkButton } from "./components/BoomFinanceLinkButton";
import { ApplicationNavigator } from "./components/ApplicationNavigator";

import { DecisionButton } from "shared/app/DecisionButton";
import { useDecisionButton } from "shared/app/DecisionButton/useDecisionButton";
import { resumeApplication } from "shared/app/OnHoldResume/api";
import {
  ApplicationLike,
  getApplicationUIStatus
} from "utils/get-application-ui-status";
import {
  FilterIcon,
  Note02Icon,
  Time04Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useNavigate } from "react-router-dom";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { AssigneeSelect } from "shared/app/ApplicationsTableView/components/AssigneeSelect";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMessagePopupStore } from "shared/app/MessagePopup/store";
import { useApplicationChatUnreads } from "./hooks/useApplicationChatUnreads";
import { ChatButton } from "../ChatButton";
import { track } from "shared/telemetry/track";
import { getFullName } from "shared/utils/get-full-name";
import { getApplicantRole } from "utils/getApplicantRole";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { ScreeningStatusBadge } from "shared/app/ApplicationsTableView/components/ScreeningStatusBadge";
import { CopyIconButton } from "shared/components/CopyIconButton";
import { ActiveDiscountsModal } from "views/discounts/components/ActiveDiscountsModal";
import { ApplicationStageSelect } from "shared/app/ApplicationStage/ApplicationStageSelect";
import { useApplicationStages } from "shared/app/ApplicationStage/useApplicationStages";
import {
  getOwnerReportShares,
  QUERY_OWNER_REPORT_SHARES
} from "api/owner-report-shares";
import { ShareWithOwnerDialog } from "views/applications-id/components/ShareWithOwnerDialog";
import { AgentReviewButton } from "shared/app/UnderwritingAgents/findings/AgentReviewButton";
import type { UnderwritingAgentStatus } from "api/underwriting-agents/types";
import { ApplicantPresenceDot } from "shared/app/LiveView/ApplicantPresenceDot";

interface ILayoutHeader {
  applicationId: string;
  selectedApplicantId: string;
  onSelectApplicant: (applicantId: string) => void;
  applicants?: Applicant[];
  application: Application;
  invitees: Invitee[];
  disableDecisionFlow?: boolean;
  applicationStatus: ApplicationStatus;
  decisionAt: string | null;
  submittedAt: string | null;
  setShowActivityLog: (value: boolean) => void;
  setShowDecisionFlowModal?: (isOpen: boolean) => void;
  setDecisionConfirmationModal?: (isOpen: boolean) => void;
  setShowInviteApplicantsModal?: (isOpen: boolean) => void;
  setShowNotesModal?: (isOpen: boolean) => void;
  onCancelClick?: () => void;
  onUncancelClick?: () => void;
  onMoveClick?: () => void;
  onSplitClick?: () => void;
  onRefundClick?: () => void;
  requirements?: MakeDecisionRequirements[];
  numberOfNotes: number;
  canViewReports: boolean;
  /** The underwriting agent's progress; undefined when the application has no agent or the user can't view reports. */
  underwritingAgentStatus?: UnderwritingAgentStatus;
  onAgentReviewClick?: () => void;
}

export const LayoutHeader = ({
  applicationId,
  selectedApplicantId,
  onSelectApplicant,
  applicants,
  application,
  applicationStatus,
  decisionAt,
  setShowActivityLog,
  setShowDecisionFlowModal,
  setDecisionConfirmationModal,
  setShowInviteApplicantsModal,
  setShowNotesModal,
  onCancelClick,
  onUncancelClick,
  onSplitClick,
  onMoveClick,
  onRefundClick,
  requirements,
  numberOfNotes,
  canViewReports,
  underwritingAgentStatus,
  onAgentReviewClick
}: ILayoutHeader) => {
  const { type, partnerFeatures } = useAuthContext();
  const navigate = useNavigate();
  const [showShareWithOwner, setShowShareWithOwner] = useState(false);

  // Sharing is open to any partner user who can see the application, unlike
  // configuring what the report contains; the admin check belongs on Settings.
  //
  // OWNER REPORTS FLAG — useOwnerReportsEnabled folds in the rollout flag, so
  // the row stays hidden even for a partner who already has the switch on.
  const ownerReportsEnabled = useOwnerReportsEnabled();
  const canShareWithOwner = type.isBoomScreen && ownerReportsEnabled;

  // Fetched for the menu row's count, which is why it loads with the page
  // rather than when the dialog opens.
  const { data: ownerShares = [] } = useQuery({
    queryKey: QUERY_OWNER_REPORT_SHARES(applicationId),
    queryFn: () => getOwnerReportShares(applicationId),
    enabled: canShareWithOwner
  });

  const applicationForStatus: ApplicationLike = {
    status: application.status,
    applicants: application.applicants
  };

  const { appActions, openUndoModal, UndoDecisionModal } = useDecisionButton({
    applicationId,
    status: applicationStatus,
    applicants,
    application,
    decisionAt,
    requirements
  });

  const displayStatus = getApplicationUIStatus(applicationForStatus);
  const {
    actions,
    showActiveDiscountCodes,
    setShowActiveDiscountCodes,
    availableDiscounts
  } = usePopoverActions({
    applicationId,
    actions: appActions,
    templateId: application.verification_template?.id || "",
    onMoveClick,
    onRefundClick,
    onCancelClick,
    onUncancelClick,
    onSplitClick,
    onInviteApplicantClick: () => setShowInviteApplicantsModal?.(true),
    onShareWithOwnerClick: () => setShowShareWithOwner(true),
    showShareWithOwner: canShareWithOwner,
    activeOwnerLinks: ownerShares.filter((share) => share.state === "active")
      .length
  });

  const queryClient = useQueryClient();
  const { data: stages } = useApplicationStages();
  const hasStages = Boolean(stages?.length);

  const canViewLead =
    type.isBoomCRM &&
    type.isTenantLeads &&
    Boolean(application.lead_id) &&
    Number(application.total_applicants) <= 1;

  const isPurchaseApplication = Boolean(application.boom_finance_external_id);

  const isMultiFamily = application.property?.kind === PropertyKind.MultiFamily;
  const address = isMultiFamily
    ? (application.unit?.address?.one_line_address ??
      application.property?.address.one_line_address)
    : application.property?.address.one_line_address;

  const applicantTabs = useMemo<Tab<string>[]>(() => {
    const overviewTab: Tab<string> = {
      value: "",
      label: "Application overview"
    };

    if (!canViewReports) return [overviewTab];

    const applicantTabItems: Tab<string>[] = (applicants || []).map((app) => ({
      value: String(app.id),
      label: (
        <span className="whitespace-nowrap">
          <ApplicantPresenceDot applicantId={Number(app.id)} />
          {`${getFullName({
            firstName: app.first_name,
            lastName: app.last_name,
            middleName: app.middle_name,
            short: true
          })} · `}
          <span className="text-xs font-medium">
            {app.is_primary ? "Primary" : getApplicantRole(app.role, false)}
          </span>
        </span>
      )
    }));

    return [overviewTab, ...applicantTabItems];
  }, [applicants, canViewReports]);

  // Chat entry point (CRM-1133). Same gating as the per-applicant chat icon in
  // ApplicantsTable. Native chat only: the pop-up renders the Inbox thread,
  // which has no legacy Stream path. The badge sums unread across every
  // applicant because the pop-up header switches between them.
  const hasChat =
    partnerFeatures.includes(PartnerFeatures.Chat) && type.isNativeChat;
  const openMessagePopup = useMessagePopupStore((state) => state.open);
  // No crm_contact_id requirement: the pop-up mints and links the CRM contact
  // from the applicant when it is missing (older applicants have none).
  const chatApplicants = useMemo(
    () =>
      (applicants ?? []).filter(
        (applicant) =>
          !applicant.is_manual && Boolean(applicant.application_started_at)
      ),
    [applicants]
  );
  const hasChatEntryPoint = hasChat && chatApplicants.length > 0;
  const chatUnreads = useApplicationChatUnreads(
    hasChatEntryPoint ? chatApplicants : []
  );
  const primaryChatApplicant =
    chatApplicants.find((applicant) => applicant.is_primary) ??
    chatApplicants[0];

  return (
    <div>
      <div className="flex h-12 items-center justify-between border-b border-alpha-40 px-3 lg:px-6 lg:flex-nowrap flex-wrap">
        <div className="flex items-center gap-2.5 lg:h-auto h-5">
          <BreadCrumbs
            crumbs={[
              {
                title: "Applications",
                href: "/applications"
              },
              {
                title: "Application overview",
                isActive: false
              }
            ]}
          />
          <ApplicationNavigator applicationId={applicationId} />
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2 lg:flex">
            <span className="text-sm font-medium text-content-300">
              Assigned to
            </span>
            <AssigneeSelect
              value={application.assignees?.[0]?.id ?? null}
              rowId={String(application.id)}
              propertyId={application.property?.id}
              inputClassName="w-[185px]"
              compact
            />
          </div>
          <Show if={isPurchaseApplication}>
            <BoomFinanceLinkButton
              boomFinanceExternalId={application.boom_finance_external_id}
            />
          </Show>
          <Show if={canViewLead}>
            <Button
              borderLess
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={FilterIcon}
              className="hidden lg:flex !text-content-400 !stroke-content-400"
              onClick={() => {
                if (application.lead_id)
                  navigate(`/leads/${application.lead_id}`);
              }}
            >
              View lead
            </Button>
          </Show>
          <Button
            borderLess
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            className="hidden lg:flex -mr-4 !text-content-400 !stroke-content-400"
            HugeIcon={Time04Icon}
            onClick={() => setShowActivityLog(true)}
          >
            Activity log
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-between px-3 py-3 lg:px-6 lg:py-3 flex-wrap lg:flex-nowrap min-h-[88px]">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h4 className="text-xl font-bold text-content-1000">{address}</h4>
            <Show if={Boolean(address)}>
              <CopyIconButton
                value={address || ""}
                idleAriaLabel="Copy address"
                size={16}
              />
            </Show>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 p-1">
              <span className="text-sm font-medium text-content-500">
                Application
              </span>
              <ApplicationStatusBadge
                type="application"
                status={displayStatus}
              />
            </div>
            <Show if={hasStages}>
              <div className="flex items-center gap-2 p-1">
                <span className="text-sm font-medium text-content-500">
                  Stage
                </span>
                <ApplicationStageSelect application={application} />
              </div>
            </Show>
            <div className="flex items-center gap-2 p-1">
              <span className="text-sm font-medium text-content-500">
                Screening
              </span>
              <ScreeningStatusBadge
                status={application.screening_status}
                paused={Boolean(
                  application.requires_override_warnings_report?.applicants
                    .length
                )}
                reverificationRequests={
                  application.open_reverification_requests
                }
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 ml-auto">
          {underwritingAgentStatus && onAgentReviewClick ? (
            <AgentReviewButton
              status={underwritingAgentStatus}
              onClick={onAgentReviewClick}
            />
          ) : null}
          <Show if={hasChatEntryPoint}>
            <ChatButton
              unreads={chatUnreads}
              onClick={() => {
                if (!primaryChatApplicant) return;
                openMessagePopup({
                  contactId: primaryChatApplicant.crm_contact_id ?? undefined,
                  applicationId: application.id,
                  applicantId: primaryChatApplicant.id,
                  channel: "chat"
                });
              }}
            />
          </Show>
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => {
              track("application.notes.page_view", {
                application_id: applicationId
              });
              setShowNotesModal?.(true);
            }}
          >
            <div className="flex items-center gap-2">
              <span className="relative inline-flex shrink-0">
                <HugeiconsIcon icon={Note02Icon} size={20} strokeWidth={1.5} />
                <Show if={Boolean(numberOfNotes)}>
                  <span className="absolute -top-1 -right-1.5 flex h-[17.5px] min-w-[17.5px] items-center justify-center rounded-sm bg-primary-500 text-[10px] font-medium leading-4 text-white border-[1.5px] border-white">
                    {numberOfNotes}
                  </span>
                </Show>
              </span>
              <span>Notes</span>
            </div>
          </Button>
          <ContextMenuPopover borderLess={false} actions={actions} />
          <DecisionButton
            appActions={appActions}
            onUndoDecision={openUndoModal}
            onMakeDecision={() => {
              applicationStatus === ApplicationStatus.Submitted
                ? setShowDecisionFlowModal?.(true)
                : setDecisionConfirmationModal?.(true);
            }}
          />

          <Show if={applicationStatus === ApplicationStatus.OnHold}>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={() => {
                resumeApplication({
                  applicationId,
                  onSuccess: () => {
                    queryClient.refetchQueries({
                      queryKey: ["APPLICATION", applicationId]
                    });
                  }
                });
              }}
              className="hidden md:flex"
            >
              Resume
            </Button>
          </Show>
        </div>
      </div>

      <div className="border-t border-alpha-40 px-3 py-2 lg:px-6">
        <Tabs
          tabs={applicantTabs}
          active={selectedApplicantId}
          onChange={onSelectApplicant}
          tabStyle="ghost-pink"
          size="sm"
          fullWidthTabs={false}
          collapseOnMobile
        />
      </div>

      <UndoDecisionModal />

      <ActiveDiscountsModal
        isOpen={showActiveDiscountCodes}
        onClose={() => setShowActiveDiscountCodes(false)}
        discounts={availableDiscounts}
      />

      <Show if={canShareWithOwner}>
        <ShareWithOwnerDialog
          opened={showShareWithOwner}
          onClose={() => setShowShareWithOwner(false)}
          applicationId={applicationId}
          subtitle={address ?? undefined}
        />
      </Show>
    </div>
  );
};
